import { useState } from 'react';

import { AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';

import { SpeechModel } from '../../types';

interface DeleteSpeechModelModalProps {
  speechModel: SpeechModel;
  onClose: () => void;
}

export const DeleteSpeechModelModal = ({ speechModel, onClose }: DeleteSpeechModelModalProps) => {
  const { t } = useTranslation();
  const { deleteSpeechModel, deleteStatus } = useSpeechModels();
  const [inUseMessage, setInUseMessage] = useState<string | null>(null);

  const handleDelete = () => {
    deleteSpeechModel(
      { speech_model_id: speechModel.speech_model_id, force: !!inUseMessage },
      {
        onSuccess: () => {
          toast.success(t('developer_tools.speech_models.deleted', { 0: speechModel.name }));
          onClose();
        },
        onError: (error) => {
          if (error.code === 'speech-model-in-use') {
            setInUseMessage(error.message);
            return;
          }
          toast.error(error.message);
        },
      }
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('developer_tools.speech_models.delete_title')}</DialogTitle>
          <DialogDescription>
            {t('developer_tools.speech_models.delete_description', { 0: speechModel.name })}
          </DialogDescription>
        </DialogHeader>

        {inUseMessage && (
          <Alert variant="destructive" className="border-destructive/50 text-destructive">
            <AlertCircleIcon size={16} />
            <AlertDescription>{inUseMessage}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost-neutral" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            isLoading={deleteStatus === 'pending'}
          >
            {inUseMessage ? t('developer_tools.speech_models.delete_anyway') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
